/* eslint-disable react-hooks/exhaustive-deps */
import { faCirclePlus, faInfoCircle, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FieldArray, FormikProps, getIn, useFormikContext } from "formik";
import React, { useEffect, useState } from "react";
import { Accordion, Button, Card, Col, Form, Row } from "react-bootstrap";

import { Section10ExemptAllowances } from "../../../../config/filingConstants";
import { IAddIncomeForm } from "../../../../config/interfaces";
import { useAppDispatch } from "../../../../redux/Store";
import { showHintModal } from "../../../../redux/slice/HintModalSlice";
import FASvgIcon from "../../../FASvgIcon";
import PreventEditComponent from "../../../PreventEditComponent";
import PriceDisplay from "../../../PriceDisplay";
import { ContextAwareToggle } from "../../../accordion";
import { FormField, NumericInputField, SelectInputField } from "../../../fields";

const SalaryExemptSection10Form: React.FC = () => {
	const { values, errors, touched, setFieldValue, handleBlur } = useFormikContext<IAddIncomeForm>();

	const dispatch = useAppDispatch();
	const [disableInput, setDisableInput] = useState(false);

	return (
		<>
			<Accordion className="accordion-sm" defaultActiveKey="allowances-section-10">
				<Accordion.Item eventKey="allowances-section-10">
					<ContextAwareToggle eventKey="allowances-section-10" isChild={true}>
						<div className="btn-content justify-content-between">
							<Form.Label className="leading-para mb-0">
								Allowances exempt u/s 10
								<span
									className="info-wrap"
									role="button"
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										dispatch(
											showHintModal({
												title: "Exempt allowances u/s 10",
												componentName: "ExemptAllowancesHint"
											})
										);
									}}
								>
									<FASvgIcon icon={faInfoCircle} />
								</span>
							</Form.Label>
							<div
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
								}}
								className="flex-grow-1"
							>
								<NumericInputField
									showLeftIcon={true}
									name="ExemptAllowancesUnderSection10"
									value={values?.ExemptAllowancesUnderSection10 || ""}
									onBlur={handleBlur("ExemptAllowancesUnderSection10")}
									onChange={(value) => setFieldValue("ExemptAllowancesUnderSection10", value)}
									isInvalid={
										Boolean(errors.ExemptAllowancesUnderSection10) &&
										touched.ExemptAllowancesUnderSection10
									}
									isDisabled={disableInput}
								/>
							</div>
						</div>
					</ContextAwareToggle>
					<Accordion.Body className="py-0">
						<ExemptionsBreakupForm handleOnSumChange={(val) => setDisableInput(() => val)} />
					</Accordion.Body>
				</Accordion.Item>
			</Accordion>
		</>
	);
};

export default SalaryExemptSection10Form;

type FormProps = { handleOnSumChange: (isDisabled: boolean) => void };

const ExemptionsBreakupForm: React.FC<FormProps> = (props: FormProps) => {
	const { handleOnSumChange } = props;

	const { values, errors, touched, setFieldValue, handleBlur } = useFormikContext<IAddIncomeForm>();

	const dispatch = useAppDispatch();

	const [sum, setSum] = useState(0);
	const [isFirstRender, setIsFirstRender] = useState<boolean>(true);
	const [currentTime, setCurrentTime] = useState<number>(new Date().getTime());

	useEffect(() => {
		let _sum = 0;
		values?.ExemptAllowancesDescriptions?.forEach((item) => {
			if (item.length && item[1]) {
				_sum += Number(item[1]) || 0;
				setIsFirstRender(() => false);
			}
		});

		setSum(() => _sum);

		// also update the gross total in upper section
		handleOnSumChange(_sum > 0);

		if (isFirstRender && !_sum) {
			setFieldValue("ExemptAllowancesUnderSection10", values?.ExemptAllowancesUnderSection10 || "");
		} else if (!isFirstRender) {
			setFieldValue("ExemptAllowancesUnderSection10", _sum);
		} else if (isFirstRender && _sum) {
			setFieldValue("ExemptAllowancesUnderSection10", _sum);
		}
	}, [currentTime]);

	return (
		<>
			<Card className="more-info-card shadow-none">
				<Card.Header>
					<Card.Title>Break up of exemptions under section 10</Card.Title>
				</Card.Header>
				<Card.Body>
					<section>
						<FormField
							label={
								<>
									House Rent Allowance
									<span
										className="info-wrap"
										role="button"
										onClick={() =>
											dispatch(
												showHintModal({
													title: "House Rent Allowance (HRA) Exemption u/s 10(13A)",
													componentName: "HRAExemptAllowanceHint"
												})
											)
										}
									>
										<FASvgIcon icon={faInfoCircle} />
									</span>
								</>
							}
							sm={6}
						>
							<NumericInputField
								showLeftIcon={true}
								name={`ExemptAllowancesDescriptions.${0}[1]`}
								value={values.ExemptAllowancesDescriptions[0][1]}
								onChange={(value) => {
									setFieldValue(`ExemptAllowancesDescriptions.${0}[1]`, value);
									setCurrentTime(new Date().getTime());
								}}
								onBlur={handleBlur(`ExemptAllowancesDescriptions.${0}[1]`)}
								isInvalid={
									Boolean(getIn(errors, `ExemptAllowancesDescriptions.${0}[1]`)) &&
									getIn(touched, `ExemptAllowancesDescriptions.${0}[1]`)
								}
								errorMsg={getIn(errors, `ExemptAllowancesDescriptions.${0}[1]`)}
							/>
						</FormField>

						<FormField label="LTA Allowance" sm={6}>
							<NumericInputField
								showLeftIcon={true}
								name={`ExemptAllowancesDescriptions.${1}[1]`}
								value={values.ExemptAllowancesDescriptions[1][1]}
								onChange={(value) => {
									setFieldValue(`ExemptAllowancesDescriptions.${1}[1]`, value);
									setCurrentTime(new Date().getTime());
								}}
								onBlur={handleBlur(`ExemptAllowancesDescriptions.${1}[1]`)}
								isInvalid={
									Boolean(getIn(errors, `ExemptAllowancesDescriptions.${1}[1]`)) &&
									getIn(touched, `ExemptAllowancesDescriptions.${1}[1]`)
								}
								errorMsg={getIn(errors, `ExemptAllowancesDescriptions.${1}[1]`)}
							/>
						</FormField>
					</section>
					<section>
						<FieldArray name="ExemptAllowancesDescriptions">
							{({ push, remove, form }) => (
								<>
									{values?.ExemptAllowancesDescriptions?.map((_, index) => {
										if ([0, 1].includes(index)) {
											return null;
										}

										return (
											<section className="mb-3 mb-sm-0" key={`salary-exempt-${index}`}>
												<SalarySection10Form
													index={index}
													form={form}
													onRemove={(itemIdx: number) => {
														remove(itemIdx);
														setCurrentTime(new Date().getTime());
													}}
													handleAmountInc={() => setCurrentTime(new Date().getTime())}
												/>
											</section>
										);
									})}
									<PreventEditComponent>
										<Button variant="link" className="btn-add-link" onClick={() => push(["", ""])}>
											<FASvgIcon icon={faCirclePlus} />
											Add more
										</Button>
									</PreventEditComponent>
								</>
							)}
						</FieldArray>
					</section>
				</Card.Body>
				<Card.Footer>
					<p className="total-content">
						<span className="total-label">Total:</span>
						<span className="total-amount">
							<PriceDisplay price={sum} />
						</span>
					</p>
				</Card.Footer>
			</Card>
		</>
	);
};

type Props = {
	index: number;
	form: FormikProps<IAddIncomeForm>;
	onRemove: (itemIdx: number) => void;
	handleAmountInc: () => void;
};

const SalarySection10Form: React.FC<Props> = (props: Props) => {
	const { index, onRemove, form, handleAmountInc } = props;

	const { setFieldValue, handleBlur, values, touched, errors } = form;

	return (
		<>
			<Row className="align-items-center">
				<Col sm={6}>
					<SelectInputField
						placeholder="Select other"
						name={`ExemptAllowancesDescriptions.${index}[0]`}
						isClearable={false}
						isOptionArrayOfObject={false}
						options={Section10ExemptAllowances}
						value={[values.ExemptAllowancesDescriptions[index][0]]}
						onChange={(selectedStatus) => {
							if (selectedStatus) {
								setFieldValue(`ExemptAllowancesDescriptions.${index}[0]`, selectedStatus.value);
							} else {
								setFieldValue(`ExemptAllowancesDescriptions.${index}[0]`, undefined);
							}
						}}
						onBlur={handleBlur(`ExemptAllowancesDescriptions.${index}[0]`)}
						isInvalid={
							Boolean(getIn(errors, `ExemptAllowancesDescriptions.${index}[0]`)) &&
							getIn(touched, `ExemptAllowancesDescriptions.${index}[0]`)
						}
						errorMsg={getIn(errors, `ExemptAllowancesDescriptions.${index}[0]`)}
					/>
				</Col>
				<Col sm>
					<NumericInputField
						showLeftIcon={true}
						placeholder=""
						name={`ExemptAllowancesDescriptions.${index}[1]`}
						value={values.ExemptAllowancesDescriptions[index][1]}
						onBlur={handleBlur(`ExemptAllowancesDescriptions.${index}[1]`)}
						onChange={(value) => {
							setFieldValue(`ExemptAllowancesDescriptions.${index}[1]`, value);
							handleAmountInc();
						}}
						isInvalid={
							Boolean(getIn(errors, `ExemptAllowancesDescriptions.${index}[1]`)) &&
							getIn(touched, `ExemptAllowancesDescriptions.${index}[1]`)
						}
						errorMsg={getIn(errors, `ExemptAllowancesDescriptions.${index}[1]`)}
					/>
				</Col>
				<PreventEditComponent>
					<Col sm={"auto"} className="ps-0 align-self-start">
						<Button
							variant="transparent"
							className="text-greyDark icon-only mt-1 d-none d-sm-flex"
							onClick={() => onRemove(index)}
						>
							<FASvgIcon icon={faTrash} />
						</Button>
						<Button
							variant="transparent"
							className="btn-delete-full d-sm-none"
							onClick={() => onRemove(index)}
						>
							<FASvgIcon icon={faTrash} />
							<span>Delete</span>
						</Button>
					</Col>
				</PreventEditComponent>
			</Row>
			{index !== values.ExemptAllowancesDescriptions.length - 1 && (
				<hr className="d-block d-sm-none my-4" />
			)}
		</>
	);
};
